import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDate, formatDateTime } from "../lib/date";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Database,
  BookOpen,
  Table as TableIcon,
  ArrowLeft,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronRight,
  Code,
  FileText,
  Link2,
  HelpCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Save,
  X,
  Sparkles,
  Cpu,
  Flag,
  Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Business dictionary area definitions
const DICTIONARY_AREAS = [
  { id: "dockets", label: "Dockets", color: "bg-blue-500" },
  { id: "bookings", label: "Bookings", color: "bg-green-500" },
  { id: "customers", label: "Customers", color: "bg-purple-500" },
  { id: "machines", label: "Machines / Assets", color: "bg-orange-500" },
  { id: "operators", label: "Operators", color: "bg-cyan-500" },
  { id: "invoicing", label: "Invoicing", color: "bg-yellow-500" },
  { id: "timesheets", label: "Timesheets", color: "bg-pink-500" }
];

export default function SQLCopilotAdmin() {
  const navigate = useNavigate();
  
  // View state
  const [activeView, setActiveView] = useState("schemas"); // schemas, dictionary, schema-detail
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [schemaDetailTab, setSchemaDetailTab] = useState("tables"); // tables, procedures
  
  // Data state
  const [schemas, setSchemas] = useState([]);
  const [databaseConfigs, setDatabaseConfigs] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableSearch, setTableSearch] = useState("");
  const [dictionaryAreas, setDictionaryAreas] = useState([]);

  // Stored procedures state
  const [procedures, setProcedures] = useState([]);
  const [procSearch, setProcSearch] = useState("");
  const [procFilter, setProcFilter] = useState("all"); // all, modified, unmodified
  const [selectedProc, setSelectedProc] = useState(null);
  const [procDetailOpen, setProcDetailOpen] = useState(false);
  const [procEditOpen, setProcEditOpen] = useState(false);
  const [procEditForm, setProcEditForm] = useState({
    is_modified: false,
    modification_notes: "",
    affects_tables: "",
    standard_version: ""
  });
  const [analyzingProc, setAnalyzingProc] = useState(null);
  const [procAnalysisResult, setProcAnalysisResult] = useState(null);

  // Stored procedure health widget
  const [procHealth, setProcHealth] = useState(null);

  // Pull-into-dictionary dialog state
  const [pullDictOpen, setPullDictOpen] = useState(false);
  const [pullDictSaving, setPullDictSaving] = useState(false);
  const [pullDictForm, setPullDictForm] = useState({
    area: "dockets",
    name: "",
    description: "",
    when_to_use: "",
    key_parameters: "",
    notes: ""
  });

  // Manual add stored procedure dialog
  const [addProcOpen, setAddProcOpen] = useState(false);
  const [addProcSaving, setAddProcSaving] = useState(false);
  const [addProcForm, setAddProcForm] = useState({
    name: "",
    schema_name: "dbo",
    description: "",
    parameters_text: "", // one per line: "@DocketID int"
    definition: "",
    is_modified: false,
    modification_notes: ""
  });
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importClientId, setImportClientId] = useState("");
  const [importIncludeProcedures, setImportIncludeProcedures] = useState(true);
  const [importIncludeDefinitions, setImportIncludeDefinitions] = useState(true);
  const [importIsReimport, setImportIsReimport] = useState(false);
  const [tableDetailOpen, setTableDetailOpen] = useState(false);
  const [addQueryDialogOpen, setAddQueryDialogOpen] = useState(false);
  
  // Dictionary editing state
  const [editingDictionary, setEditingDictionary] = useState(null);
  const [newQuery, setNewQuery] = useState({ name: "", description: "", sql: "", parameters: [] });

  // Fetch schemas on mount
  useEffect(() => {
    fetchSchemas();
    fetchDatabaseConfigs();
    fetchDictionaryAreas();
    fetchProcHealth();
  }, []);

  // Fetch tables and procedures when schema selected
  useEffect(() => {
    if (selectedSchema) {
      fetchTables(selectedSchema.client_id);
      fetchProcedures(selectedSchema.client_id);
    }
  }, [selectedSchema]);

  const fetchSchemas = async () => {
    try {
      const res = await axios.get(`${API}/sql-copilot/schemas`);
      setSchemas(res.data.schemas || []);
    } catch (error) {
      console.error("Failed to fetch schemas:", error);
      toast.error("Failed to load schemas");
    }
  };

  const fetchDatabaseConfigs = async () => {
    try {
      const res = await axios.get(`${API}/databases/configs`);
      setDatabaseConfigs(res.data.configs || []);
    } catch (error) {
      console.error("Failed to fetch database configs:", error);
    }
  };

  const fetchTables = async (clientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sql-copilot/schemas/${clientId}/tables`);
      setTables(res.data.tables || []);
    } catch (error) {
      console.error("Failed to fetch tables:", error);
      toast.error("Failed to load tables");
    } finally {
      setLoading(false);
    }
  };

  const fetchTableDetail = async (tableName) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sql-copilot/schemas/${selectedSchema.client_id}/tables/${tableName}`);
      setSelectedTable(res.data);
      setTableDetailOpen(true);
    } catch (error) {
      console.error("Failed to fetch table detail:", error);
      toast.error("Failed to load table details");
    } finally {
      setLoading(false);
    }
  };

  const fetchProcedures = async (clientId) => {
    try {
      const res = await axios.get(`${API}/sql-copilot/schemas/${clientId}/procedures`);
      setProcedures(res.data.procedures || []);
    } catch (error) {
      console.error("Failed to fetch procedures:", error);
    }
  };

  const fetchProcedureDetail = async (procName) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sql-copilot/schemas/${selectedSchema.client_id}/procedures/${procName}`);
      setSelectedProc(res.data);
      setProcAnalysisResult(null);
      setProcDetailOpen(true);
    } catch (error) {
      console.error("Failed to fetch procedure:", error);
      toast.error("Failed to load procedure");
    } finally {
      setLoading(false);
    }
  };

  const openProcEdit = (proc) => {
    setSelectedProc(proc);
    setProcEditForm({
      is_modified: !!proc.is_modified,
      modification_notes: proc.modification_notes || "",
      affects_tables: (proc.affects_tables || []).join(", "),
      standard_version: proc.standard_version || ""
    });
    setProcEditOpen(true);
  };

  const saveProcModification = async () => {
    if (!selectedProc) return;
    setSaving(true);
    try {
      const affects = procEditForm.affects_tables
        .split(",")
        .map(t => t.trim())
        .filter(Boolean);
      await axios.patch(
        `${API}/sql-copilot/schemas/${selectedSchema.client_id}/procedures/${selectedProc.name}`,
        {
          is_modified: procEditForm.is_modified,
          modification_notes: procEditForm.modification_notes || null,
          affects_tables: affects,
          standard_version: procEditForm.standard_version || null
        }
      );
      toast.success("Procedure updated");
      setProcEditOpen(false);
      fetchProcedures(selectedSchema.client_id);
      fetchProcHealth();
      // Refresh detail if open
      if (procDetailOpen) {
        fetchProcedureDetail(selectedProc.name);
      }
    } catch (error) {
      console.error("Failed to update procedure:", error);
      toast.error(error.response?.data?.detail || "Failed to update procedure");
    } finally {
      setSaving(false);
    }
  };

  const runProcedureAnalysis = async (procName) => {
    setAnalyzingProc(procName);
    setProcAnalysisResult(null);
    try {
      const res = await axios.post(
        `${API}/sql-copilot/schemas/${selectedSchema.client_id}/procedures/${procName}/analyze`
      );
      if (res.data.success) {
        toast.success("AI analysis complete");
        setProcAnalysisResult(res.data);
        // Refresh procedure detail to pick up persisted summary
        fetchProcedureDetail(procName);
        fetchProcedures(selectedSchema.client_id);
        fetchProcHealth();
      } else {
        toast.error(res.data.error || "Analysis failed");
        setProcAnalysisResult(res.data);
      }
    } catch (error) {
      console.error("Procedure analysis failed:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze procedure");
    } finally {
      setAnalyzingProc(null);
    }
  };

  const openPullToDictionary = (proc) => {
    // Pre-fill from the procedure metadata
    const paramStr = (proc.parameters || [])
      .map((p) => `@${p.name} ${p.data_type}${p.max_length ? `(${p.max_length})` : ""}`)
      .join(", ");

    // Use the AI summary as a starter description if present
    const initialDesc = proc.ai_summary || "";

    setPullDictForm({
      area: "dockets",
      name: proc.name || "",
      description: initialDesc,
      when_to_use: "",
      key_parameters: paramStr,
      notes: proc.modification_notes || ""
    });
    setPullDictOpen(true);
  };

  const savePullToDictionary = async () => {
    if (!pullDictForm.name.trim() || !pullDictForm.description.trim()) {
      toast.error("Procedure name and description are required");
      return;
    }
    setPullDictSaving(true);
    try {
      await axios.post(
        `${API}/sql-copilot/dictionary/${pullDictForm.area}/procedures`,
        {
          name: pullDictForm.name.trim(),
          description: pullDictForm.description.trim(),
          when_to_use: pullDictForm.when_to_use || null,
          key_parameters: pullDictForm.key_parameters || null,
          notes: pullDictForm.notes || null
        }
      );
      toast.success(`Added to ${pullDictForm.area} dictionary`);
      setPullDictOpen(false);
      fetchDictionaryAreas();
    } catch (error) {
      console.error("Failed to add to dictionary:", error);
      toast.error(error.response?.data?.detail || "Failed to add to dictionary");
    } finally {
      setPullDictSaving(false);
    }
  };

  const openAddProcedure = () => {
    setAddProcForm({
      name: "",
      schema_name: "dbo",
      description: "",
      parameters_text: "",
      definition: "",
      is_modified: false,
      modification_notes: ""
    });
    setAddProcOpen(true);
  };

  const saveManualProcedure = async () => {
    if (!addProcForm.name.trim()) {
      toast.error("Procedure name is required");
      return;
    }

    // Parse params: each line like "@DocketID int" or "@DocketID:int" or "DocketID int"
    const parameters = addProcForm.parameters_text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const cleaned = line.replace(/^@/, "").replace(/,$/, "").trim();
        const parts = cleaned.split(/[:\s]+/).filter(Boolean);
        const name = parts[0] || "";
        const data_type = parts.slice(1).join(" ") || "varchar";
        const is_output = /\bOUT(PUT)?\b/i.test(line);
        return { name, data_type, is_output };
      })
      .filter((p) => p.name);

    setAddProcSaving(true);
    try {
      await axios.post(
        `${API}/sql-copilot/schemas/${selectedSchema.client_id}/procedures`,
        {
          name: addProcForm.name.trim(),
          schema_name: addProcForm.schema_name || "dbo",
          description: addProcForm.description || null,
          parameters,
          definition: addProcForm.definition || null,
          is_modified: addProcForm.is_modified,
          modification_notes: addProcForm.modification_notes || null
        }
      );
      toast.success("Procedure added");
      setAddProcOpen(false);
      fetchProcedures(selectedSchema.client_id);
      fetchProcHealth();
      fetchSchemas(); // procedure_count on schema card refresh
    } catch (error) {
      console.error("Failed to add procedure:", error);
      toast.error(error.response?.data?.detail || "Failed to add procedure");
    } finally {
      setAddProcSaving(false);
    }
  };

  const fetchDictionaryAreas = async () => {
    try {
      const res = await axios.get(`${API}/sql-copilot/dictionary`);
      setDictionaryAreas(res.data.areas || []);
    } catch (error) {
      console.error("Failed to fetch dictionary:", error);
    }
  };

  const fetchProcHealth = async () => {
    try {
      const res = await axios.get(`${API}/sql-copilot/procedures-health`);
      setProcHealth(res.data);
    } catch (error) {
      console.error("Failed to fetch procedure health:", error);
    }
  };

  const handleImportSchema = async () => {
    if (!importClientId) {
      toast.error("Please select a database");
      return;
    }
    
    setImporting(true);
    try {
      const res = await axios.post(`${API}/sql-copilot/import-schema`, {
        client_id: importClientId,
        include_views: true,
        include_procedures: importIncludeProcedures,
        include_definitions: importIncludeDefinitions
      });
      
      toast.success(
        `Imported ${res.data.tables_imported} tables, ${res.data.procedures_imported} procedures, ${res.data.relationships_imported} relationships in ${res.data.duration_seconds}s`
      );
      setImportDialogOpen(false);
      setImportIsReimport(false);
      fetchSchemas();
      fetchProcHealth();
      // Refresh procedures if we were viewing this schema's detail
      if (selectedSchema?.client_id === importClientId) {
        fetchProcedures(importClientId);
      }
    } catch (error) {
      console.error("Failed to import schema:", error);
      toast.error(error.response?.data?.detail || "Failed to import schema");
    } finally {
      setImporting(false);
    }
  };

  const openImportDialog = () => {
    setImportClientId("");
    setImportIncludeProcedures(true);
    setImportIncludeDefinitions(true);
    setImportIsReimport(false);
    setImportDialogOpen(true);
  };

  const openReimportDialog = (clientId) => {
    setImportClientId(clientId);
    setImportIncludeProcedures(true);
    setImportIncludeDefinitions(true);
    setImportIsReimport(true);
    setImportDialogOpen(true);
  };

  const handleDeleteSchema = async (clientId) => {
    if (!window.confirm(`Delete schema for ${clientId}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/sql-copilot/schemas/${clientId}`);
      toast.success("Schema deleted");
      fetchSchemas();
      if (selectedSchema?.client_id === clientId) {
        setSelectedSchema(null);
        setActiveView("schemas");
      }
    } catch (error) {
      toast.error("Failed to delete schema");
    }
  };

  const handleUpdateTableNotes = async (tableName, description, businessNotes) => {
    try {
      await axios.patch(`${API}/sql-copilot/schemas/${selectedSchema.client_id}/tables/${tableName}`, {
        description,
        business_notes: businessNotes
      });
      toast.success("Table notes updated");
      fetchTables(selectedSchema.client_id);
    } catch (error) {
      toast.error("Failed to update notes");
    }
  };

  const handleSaveDictionary = async () => {
    if (!editingDictionary) return;
    
    setSaving(true);
    try {
      // Only send fully-filled procedures (need at least name + description)
      const procs = (editingDictionary.important_procedures || [])
        .filter(p => p && p.name && p.name.trim() && p.description && p.description.trim())
        .map(p => ({
          name: p.name.trim(),
          description: p.description.trim(),
          when_to_use: p.when_to_use || null,
          key_parameters: p.key_parameters || null,
          notes: p.notes || null
        }));

      await axios.put(`${API}/sql-copilot/dictionary/${selectedArea}`, {
        title: editingDictionary.title,
        description: editingDictionary.description,
        key_tables: editingDictionary.key_tables || [],
        important_procedures: procs,
        notes: editingDictionary.notes,
        updated_by: "admin" // TODO: Get from auth
      });
      toast.success("Dictionary saved");
      fetchDictionaryAreas();
    } catch (error) {
      toast.error("Failed to save dictionary");
    } finally {
      setSaving(false);
    }
  };

  const addImportantProcedure = () => {
    setEditingDictionary({
      ...editingDictionary,
      important_procedures: [
        ...(editingDictionary?.important_procedures || []),
        { name: "", description: "", when_to_use: "", key_parameters: "", notes: "" }
      ]
    });
  };

  const updateImportantProcedure = (idx, field, value) => {
    const list = [...(editingDictionary?.important_procedures || [])];
    list[idx] = { ...list[idx], [field]: value };
    setEditingDictionary({ ...editingDictionary, important_procedures: list });
  };

  const removeImportantProcedure = (idx) => {
    const list = [...(editingDictionary?.important_procedures || [])];
    list.splice(idx, 1);
    setEditingDictionary({ ...editingDictionary, important_procedures: list });
  };

  const handleAddApprovedQuery = async () => {
    if (!newQuery.name || !newQuery.sql) {
      toast.error("Name and SQL are required");
      return;
    }
    
    try {
      await axios.post(`${API}/sql-copilot/dictionary/${selectedArea}/queries`, {
        name: newQuery.name,
        description: newQuery.description,
        sql: newQuery.sql,
        parameters: newQuery.parameters,
        created_by: "admin" // TODO: Get from auth
      });
      toast.success("Query added");
      setAddQueryDialogOpen(false);
      setNewQuery({ name: "", description: "", sql: "", parameters: [] });
      
      // Refresh the dictionary area
      const res = await axios.get(`${API}/sql-copilot/dictionary/${selectedArea}`);
      setEditingDictionary(res.data);
    } catch (error) {
      toast.error("Failed to add query");
    }
  };

  const handleDeleteQuery = async (queryName) => {
    if (!window.confirm(`Delete query "${queryName}"?`)) return;
    
    try {
      await axios.delete(`${API}/sql-copilot/dictionary/${selectedArea}/queries/${queryName}`);
      toast.success("Query deleted");
      
      // Refresh
      const res = await axios.get(`${API}/sql-copilot/dictionary/${selectedArea}`);
      setEditingDictionary(res.data);
    } catch (error) {
      toast.error("Failed to delete query");
    }
  };

  const filteredTables = tables.filter(t => 
    t.name.toLowerCase().includes(tableSearch.toLowerCase())
  );

  const filteredProcedures = procedures.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(procSearch.toLowerCase());
    const matchesFilter =
      procFilter === "all" ||
      (procFilter === "modified" && p.is_modified) ||
      (procFilter === "unmodified" && !p.is_modified);
    return matchesSearch && matchesFilter;
  });
  const modifiedProcCount = procedures.filter(p => p.is_modified).length;

  // Get available databases for import (not already imported)
  const availableForImport = databaseConfigs.filter(
    c => !schemas.find(s => s.client_id === c.client_id)
  );

  return (
    <div className="min-h-screen bg-[#EDEAE3] text-[#1A1A1A]">
      {/* Header */}
      <div className="border-b border-[#DAD5CA] bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/?tab=infrastructure")}
                className="text-[#59554D] hover:text-[#1A1A1A]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Infrastructure
              </Button>
              <div className="flex items-center gap-2">
                <Database className="w-6 h-6 text-[#8A6D00]" />
                <h1 className="text-xl font-bold">SQL Copilot Admin</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={activeView === "schemas" ? "default" : "outline"}
                onClick={() => setActiveView("schemas")}
                className={activeView === "schemas" ? "bg-[#FFD108]" : "border-[#DAD5CA] text-[#59554D]"}
              >
                <Database className="w-4 h-4 mr-2" />
                Schemas
              </Button>
              <Button
                variant={activeView === "dictionary" ? "default" : "outline"}
                onClick={() => setActiveView("dictionary")}
                className={activeView === "dictionary" ? "bg-[#FFD108]" : "border-[#DAD5CA] text-[#59554D]"}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Business Dictionary
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Schemas View */}
        {activeView === "schemas" && !selectedSchema && (
          <div className="space-y-6">
            {/* Stored Procedures Health Widget */}
            {procHealth && procHealth.overall.client_count > 0 && (
              <div
                className="bg-gradient-to-br from-[#FFFFFF] to-[#F4F2ED] rounded-lg border border-[#DAD5CA] p-5"
                data-testid="proc-health-widget"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-[#8A6D00]" />
                    <h2 className="text-base font-semibold">Stored Procedures Health</h2>
                  </div>
                  {procHealth.overall.last_analysis_date && (
                    <span className="text-xs text-[#8A857A]">
                      Last AI analysis: {formatDateTime(procHealth.overall.last_analysis_date)}
                    </span>
                  )}
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3">
                    <div className="text-xs text-[#59554D]">Total procedures</div>
                    <div className="text-2xl font-semibold mt-1" data-testid="proc-health-total">
                      {procHealth.overall.total_procedures}
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#FFF7D1] p-3">
                    <div className="text-xs text-[#6B5600]">Modified from standard</div>
                    <div className="text-2xl font-semibold mt-1 text-[#6B5600]" data-testid="proc-health-modified">
                      {procHealth.overall.modified_count}
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#FFF8D6] p-3">
                    <div className="text-xs text-[#8A6D00]">AI-analyzed</div>
                    <div className="text-2xl font-semibold mt-1 text-[#8A6D00]" data-testid="proc-health-analyzed">
                      {procHealth.overall.analyzed_count}
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#E3F3E9] p-3">
                    <div className="text-xs text-[#0E6B3F]">In dictionary</div>
                    <div className="text-2xl font-semibold mt-1 text-[#0E6B3F]" data-testid="proc-health-documented">
                      {procHealth.overall.documented_count}
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3">
                    <div className="text-xs text-[#59554D]">Awaiting analysis</div>
                    <div className="text-2xl font-semibold mt-1" data-testid="proc-health-pending">
                      {procHealth.overall.pending_analysis_count}
                    </div>
                  </div>
                </div>

                {/* Per-client breakdown */}
                <div className="space-y-2">
                  {procHealth.clients.map((c) => {
                    const drift = c.total_procedures > 0
                      ? Math.round((c.modified_count / c.total_procedures) * 100)
                      : 0;
                    const coverage = c.total_procedures > 0
                      ? Math.round((c.analyzed_count / c.total_procedures) * 100)
                      : 0;
                    const dictCoverage = c.total_procedures > 0
                      ? Math.round((c.documented_count / c.total_procedures) * 100)
                      : 0;
                    const driftColor = drift >= 30 ? "#C23A00" : drift >= 10 ? "#6B5600" : "#0E6B3F";

                    const targetSchema = schemas.find((s) => s.client_id === c.client_id);

                    return (
                      <button
                        key={c.client_id}
                        type="button"
                        onClick={() => {
                          if (targetSchema) {
                            setSelectedSchema(targetSchema);
                            setSchemaDetailTab("procedures");
                            setActiveView("schema-detail");
                          }
                        }}
                        disabled={!targetSchema}
                        data-testid={`proc-health-client-${c.client_id}`}
                        className="w-full text-left bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3 hover:border-[#FFF8D6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Database className="w-4 h-4 text-[#8A6D00] shrink-0" />
                            <span className="font-medium truncate">{c.client_id}</span>
                            <span className="text-xs text-[#8A857A] truncate">
                              {c.database_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-[#59554D]">{c.total_procedures} procs</span>
                            {c.modified_count > 0 && (
                              <Badge className="bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1] hover:bg-[#FFF7D1]">
                                <Flag className="w-3 h-3 mr-1" />
                                {c.modified_count} modified
                              </Badge>
                            )}
                            {c.modified_pending_analysis > 0 && (
                              <Badge className="bg-[#FFF8D6] text-[#8A6D00] border-[#FFF8D6] hover:bg-[#FFF8D6]">
                                <Sparkles className="w-3 h-3 mr-1" />
                                {c.modified_pending_analysis} need analysis
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs">
                          {/* Drift bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-[#8A857A] mb-1">
                              <span>Drift from standard</span>
                              <span style={{ color: driftColor }}>{drift}%</span>
                            </div>
                            <div className="h-1.5 bg-[#F4F2ED] rounded overflow-hidden">
                              <div
                                className="h-full transition-all"
                                style={{ width: `${drift}%`, backgroundColor: driftColor }}
                              />
                            </div>
                          </div>
                          {/* Coverage bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-[#8A857A] mb-1">
                              <span>AI coverage</span>
                              <span className="text-[#8A6D00]">{coverage}%</span>
                            </div>
                            <div className="h-1.5 bg-[#F4F2ED] rounded overflow-hidden">
                              <div
                                className="h-full bg-[#FFD108] transition-all"
                                style={{ width: `${coverage}%` }}
                              />
                            </div>
                          </div>
                          {/* Dictionary coverage bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-[#8A857A] mb-1">
                              <span>Dictionary coverage</span>
                              <span className="text-[#0E6B3F]">
                                {c.documented_count}/{c.total_procedures}{" "}
                                <span className="text-[#8A857A]">({dictCoverage}%)</span>
                              </span>
                            </div>
                            <div className="h-1.5 bg-[#F4F2ED] rounded overflow-hidden">
                              <div
                                className="h-full bg-[#0E6B3F] transition-all"
                                style={{ width: `${dictCoverage}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-[#8A857A] whitespace-nowrap hidden sm:block">
                            {c.last_analysis_date
                              ? `Analyzed ${formatDate(c.last_analysis_date)}`
                              : "Never analyzed"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Imported Database Schemas</h2>
              <Button
                onClick={openImportDialog}
                className="bg-[#FFD108] hover:bg-[#FFD108]"
                disabled={availableForImport.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Import Schema
              </Button>
            </div>

            {schemas.length === 0 ? (
              <div className="text-center py-12 bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]">
                <Database className="w-12 h-12 mx-auto text-[#8A857A] mb-4" />
                <h3 className="text-lg font-medium mb-2">No Schemas Imported</h3>
                <p className="text-[#59554D] mb-4">Import a database schema to get started with SQL Copilot</p>
                <Button
                  onClick={openImportDialog}
                  className="bg-[#FFD108]"
                  disabled={availableForImport.length === 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Import Schema
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {schemas.map((schema) => (
                  <div
                    key={schema.client_id}
                    className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-4 hover:border-[#FFF8D6] transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedSchema(schema);
                      setActiveView("schema-detail");
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-[#8A6D00]" />
                        <span className="font-medium">{schema.client_id}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReimportDialog(schema.client_id);
                          }}
                          className="text-[#8A6D00] hover:text-[#8A6D00] hover:bg-[#FFF8D6] h-8 w-8 p-0"
                          data-testid={`reimport-schema-${schema.client_id}`}
                          title="Re-import schema"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSchema(schema.client_id);
                          }}
                          className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0] h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-[#59554D] mb-3">{schema.database_name}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-[#59554D]">
                        <TableIcon className="w-4 h-4" />
                        <span>{schema.table_count} tables</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#59554D]">
                        <Link2 className="w-4 h-4" />
                        <span>{schema.relationship_count} relations</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#DAD5CA] text-xs text-[#8A857A]">
                      Imported: {formatDate(schema.imported_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Schema Detail View */}
        {activeView === "schema-detail" && selectedSchema && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedSchema(null);
                    setActiveView("schemas");
                  }}
                  className="text-[#59554D] hover:text-[#1A1A1A]"
                  data-testid="schema-detail-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div>
                  <h2 className="text-lg font-semibold">{selectedSchema.client_id}</h2>
                  <p className="text-sm text-[#59554D]">{selectedSchema.database_name}</p>
                </div>
              </div>
              <Button
                onClick={() => openReimportDialog(selectedSchema.client_id)}
                variant="outline"
                className="border-[#FFF8D6] text-[#8A6D00] hover:bg-[#FFF8D6] hover:text-[#8A6D00]"
                data-testid="schema-detail-reimport-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Re-import Schema
              </Button>
            </div>

            {/* Inner Tabs: Tables / Stored Procedures */}
            <div className="flex items-center gap-2 border-b border-[#DAD5CA]">
              <button
                onClick={() => setSchemaDetailTab("tables")}
                data-testid="schema-tab-tables"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  schemaDetailTab === "tables"
                    ? "border-[#8A6D00] text-[#1A1A1A]"
                    : "border-transparent text-[#59554D] hover:text-[#1A1A1A]"
                }`}
              >
                <TableIcon className="w-4 h-4 inline mr-2" />
                Tables ({tables.length || selectedSchema.table_count || 0})
              </button>
              <button
                onClick={() => setSchemaDetailTab("procedures")}
                data-testid="schema-tab-procedures"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  schemaDetailTab === "procedures"
                    ? "border-[#8A6D00] text-[#1A1A1A]"
                    : "border-transparent text-[#59554D] hover:text-[#1A1A1A]"
                }`}
              >
                <Cpu className="w-4 h-4 inline mr-2" />
                Stored Procedures ({procedures.length || selectedSchema.procedure_count || 0})
                {modifiedProcCount > 0 && (
                  <Badge className="ml-2 bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1] hover:bg-[#FFF7D1]">
                    {modifiedProcCount} modified
                  </Badge>
                )}
              </button>
            </div>

            {/* Tables Tab */}
            {schemaDetailTab === "tables" && (
              <>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A857A]" />
                  <Input
                    placeholder="Search tables..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    className="pl-10 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                    data-testid="table-search-input"
                  />
                </div>

                {/* Tables List */}
                <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#DAD5CA] hover:bg-transparent">
                        <TableHead className="text-[#59554D]">Table Name</TableHead>
                        <TableHead className="text-[#59554D]">Schema</TableHead>
                        <TableHead className="text-[#59554D] text-right">Columns</TableHead>
                        <TableHead className="text-[#59554D] text-right">Est. Rows</TableHead>
                        <TableHead className="text-[#59554D]">Notes</TableHead>
                        <TableHead className="text-[#59554D]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTables.slice(0, 50).map((table) => (
                        <TableRow
                          key={table.name}
                          className="border-[#DAD5CA] hover:bg-[#F4F2ED] cursor-pointer"
                          onClick={() => fetchTableDetail(table.name)}
                        >
                          <TableCell className="font-medium">{table.name}</TableCell>
                          <TableCell className="text-[#59554D]">{table.schema_name}</TableCell>
                          <TableCell className="text-right">{table.column_count}</TableCell>
                          <TableCell className="text-right text-[#59554D]">
                            {table.row_count_estimate?.toLocaleString() || "-"}
                          </TableCell>
                          <TableCell>
                            {table.has_notes ? (
                              <span className="text-[#0E6B3F]">✓</span>
                            ) : (
                              <span className="text-[#8A857A]">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-[#8A857A]" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredTables.length > 50 && (
                    <div className="p-3 text-center text-sm text-[#59554D] border-t border-[#DAD5CA]">
                      Showing 50 of {filteredTables.length} tables. Use search to filter.
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Stored Procedures Tab */}
            {schemaDetailTab === "procedures" && (
              <>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A857A]" />
                    <Input
                      placeholder="Search procedures..."
                      value={procSearch}
                      onChange={(e) => setProcSearch(e.target.value)}
                      className="pl-10 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                      data-testid="procedure-search-input"
                    />
                  </div>
                  <Select value={procFilter} onValueChange={setProcFilter}>
                    <SelectTrigger
                      className="w-full sm:w-[200px] bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                      data-testid="procedure-filter-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectItem value="all">All procedures</SelectItem>
                      <SelectItem value="modified">Modified only</SelectItem>
                      <SelectItem value="unmodified">Unmodified only</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={openAddProcedure}
                    className="bg-[#FFD108] hover:bg-[#FFD108]"
                    data-testid="add-procedure-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Procedure
                  </Button>
                </div>

                {procedures.length === 0 ? (
                  <div className="text-center py-12 bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]">
                    <Cpu className="w-12 h-12 mx-auto text-[#8A857A] mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Stored Procedures Yet</h3>
                    <p className="text-[#59554D] mb-4">
                      Re-import the schema with "Include procedures" enabled, or add procedures manually below.
                    </p>
                    <Button
                      onClick={openAddProcedure}
                      className="bg-[#FFD108] hover:bg-[#FFD108]"
                      data-testid="add-procedure-empty-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Procedure Manually
                    </Button>
                  </div>
                ) : (
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DAD5CA] hover:bg-transparent">
                          <TableHead className="text-[#59554D]">Procedure Name</TableHead>
                          <TableHead className="text-[#59554D]">Schema</TableHead>
                          <TableHead className="text-[#59554D] text-right">Params</TableHead>
                          <TableHead className="text-[#59554D]">Status</TableHead>
                          <TableHead className="text-[#59554D]">AI Summary</TableHead>
                          <TableHead className="text-[#59554D] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProcedures.slice(0, 100).map((proc) => (
                          <TableRow
                            key={proc.name}
                            className="border-[#DAD5CA] hover:bg-[#F4F2ED]"
                            data-testid={`procedure-row-${proc.name}`}
                          >
                            <TableCell className="font-medium font-mono text-sm">
                              {proc.name}
                            </TableCell>
                            <TableCell className="text-[#59554D]">{proc.schema_name}</TableCell>
                            <TableCell className="text-right text-[#59554D]">
                              {proc.parameter_count ?? 0}
                            </TableCell>
                            <TableCell>
                              {proc.is_modified ? (
                                <Badge className="bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1] hover:bg-[#FFF7D1]">
                                  <Flag className="w-3 h-3 mr-1" />
                                  Modified
                                </Badge>
                              ) : (
                                <Badge className="bg-[#DAD5CA] text-[#59554D] border-[#DAD5CA] hover:bg-[#DAD5CA]">
                                  Standard
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {proc.ai_summary ? (
                                <span
                                  className="text-xs text-[#59554D] line-clamp-1 max-w-[260px] inline-block"
                                  title={proc.ai_summary}
                                >
                                  {proc.ai_summary}
                                </span>
                              ) : (
                                <span className="text-xs text-[#8A857A]">No analysis yet</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchProcedureDetail(proc.name)}
                                  className="h-8 w-8 p-0 text-[#59554D] hover:text-[#1A1A1A]"
                                  data-testid={`view-procedure-${proc.name}`}
                                  title="View details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openProcEdit(proc)}
                                  className="h-8 w-8 p-0 text-[#59554D] hover:text-[#1A1A1A]"
                                  data-testid={`flag-procedure-${proc.name}`}
                                  title="Flag / edit"
                                >
                                  <Flag className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPullToDictionary(proc)}
                                  className="h-8 w-8 p-0 text-[#6B5600] hover:text-[#1A1A1A]"
                                  data-testid={`pull-procedure-${proc.name}`}
                                  title="Pull into Business Dictionary"
                                >
                                  <BookOpen className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => runProcedureAnalysis(proc.name)}
                                  disabled={analyzingProc === proc.name}
                                  className="h-8 w-8 p-0 text-[#8A6D00] hover:text-[#1A1A1A]"
                                  data-testid={`analyze-procedure-${proc.name}`}
                                  title="AI analyze"
                                >
                                  {analyzingProc === proc.name ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredProcedures.length > 100 && (
                      <div className="p-3 text-center text-sm text-[#59554D] border-t border-[#DAD5CA]">
                        Showing 100 of {filteredProcedures.length}. Use search to filter.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Dictionary View */}
        {activeView === "dictionary" && !selectedArea && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Business Dictionary</h2>
                <p className="text-sm text-[#59554D]">Document key database areas for the AI assistant</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DICTIONARY_AREAS.map((area) => {
                const areaData = dictionaryAreas.find(a => a.area === area.id) || {};
                const hasContent = areaData.description || (areaData.key_tables?.length > 0);
                
                return (
                  <div
                    key={area.id}
                    className={`bg-[#FFFFFF] rounded-lg border p-4 cursor-pointer transition-colors ${
                      hasContent ? "border-[#DAD5CA] hover:border-[#FFF8D6]" : "border-dashed border-[#DAD5CA] hover:border-[#8A857A]"
                    }`}
                    onClick={async () => {
                      setSelectedArea(area.id);
                      const res = await axios.get(`${API}/sql-copilot/dictionary/${area.id}`);
                      setEditingDictionary(res.data);
                      setActiveView("dictionary-edit");
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-3 h-3 rounded-full ${area.color}`}></div>
                      <span className="font-medium">{area.label}</span>
                    </div>
                    
                    {hasContent ? (
                      <>
                        <p className="text-sm text-[#59554D] line-clamp-2 mb-3">
                          {areaData.description || "No description"}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-[#8A857A]">
                          <span>{areaData.key_tables?.length || 0} tables</span>
                          <span>{areaData.important_procedures?.length || 0} procedures</span>
                          <span>{areaData.approved_queries?.length || 0} queries</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-[#8A857A]">Click to add documentation</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dictionary Edit View */}
        {activeView === "dictionary-edit" && selectedArea && editingDictionary && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedArea(null);
                    setEditingDictionary(null);
                    setActiveView("dictionary");
                  }}
                  className="text-[#59554D] hover:text-[#1A1A1A]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div>
                  <h2 className="text-lg font-semibold">
                    {DICTIONARY_AREAS.find(a => a.id === selectedArea)?.label}
                  </h2>
                  <p className="text-sm text-[#59554D]">Document this business area</p>
                </div>
              </div>
              <Button
                onClick={handleSaveDictionary}
                disabled={saving}
                className="bg-[#0E6B3F] hover:bg-[#0E6B3F]"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-4">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#8A6D00]" />
                    Basic Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#59554D] mb-1 block">Title</label>
                      <Input
                        value={editingDictionary.title || ""}
                        onChange={(e) => setEditingDictionary({
                          ...editingDictionary,
                          title: e.target.value
                        })}
                        className="bg-[#FFFFFF] border-[#DAD5CA]"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#59554D] mb-1 block">Description</label>
                      <Textarea
                        value={editingDictionary.description || ""}
                        onChange={(e) => setEditingDictionary({
                          ...editingDictionary,
                          description: e.target.value
                        })}
                        placeholder="What does this area cover? What tables are involved?"
                        className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[100px]"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm text-[#59554D] mb-1 block">
                        Key Tables (comma-separated)
                      </label>
                      <Input
                        value={(editingDictionary.key_tables || []).join(", ")}
                        onChange={(e) => setEditingDictionary({
                          ...editingDictionary,
                          key_tables: e.target.value.split(",").map(t => t.trim()).filter(Boolean)
                        })}
                        placeholder="webDocket, Booking, Customer"
                        className="bg-[#FFFFFF] border-[#DAD5CA]"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-4">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-[#6B5600]" />
                    Senior Staff Notes
                  </h3>
                  <Textarea
                    value={editingDictionary.notes || ""}
                    onChange={(e) => setEditingDictionary({
                      ...editingDictionary,
                      notes: e.target.value
                    })}
                    placeholder="Add notes about common issues, gotchas, or tips for this area..."
                    className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[150px]"
                  />
                </div>
              </div>

              {/* Approved Queries */}
              <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Code className="w-4 h-4 text-[#0E6B3F]" />
                    Approved Queries
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setAddQueryDialogOpen(true)}
                    className="bg-[#FFD108] hover:bg-[#FFD108]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Query
                  </Button>
                </div>

                {(editingDictionary.approved_queries || []).length === 0 ? (
                  <div className="text-center py-8 text-[#8A857A]">
                    <Code className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No approved queries yet</p>
                    <p className="text-sm mt-1">Add example queries for the AI to learn from</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {editingDictionary.approved_queries.map((query, idx) => (
                      <div
                        key={idx}
                        className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-medium">{query.name}</span>
                            <p className="text-sm text-[#59554D]">{query.description}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuery(query.name)}
                            className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0] h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <pre className="text-xs bg-[#FFFFFF] p-2 rounded overflow-x-auto text-[#59554D]">
                          {query.sql}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Important Stored Procedures - full width row */}
            <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-[#6B5600]" />
                    Important Stored Procedures
                  </h3>
                  <p className="text-xs text-[#59554D] mt-1">
                    Document key stored procedures for this area. Used by SQL Copilot when generating queries — remember to click Save Changes after editing.
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={addImportantProcedure}
                  className="bg-[#FFF7D1] hover:bg-[#E68A00] text-black"
                  data-testid="dict-add-procedure-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Procedure
                </Button>
              </div>

              {(editingDictionary.important_procedures || []).length === 0 ? (
                <div className="text-center py-8 text-[#8A857A]">
                  <Cpu className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No procedures documented yet</p>
                  <p className="text-sm mt-1">
                    Add the important stored procedures for this area and what they do.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {editingDictionary.important_procedures.map((proc, idx) => (
                    <div
                      key={idx}
                      className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3 space-y-3"
                      data-testid={`dict-procedure-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-[#59554D] mb-1 block">
                              Procedure Name
                            </label>
                            <Input
                              value={proc.name || ""}
                              onChange={(e) => updateImportantProcedure(idx, "name", e.target.value)}
                              placeholder="e.g., sp_CreateDocket"
                              className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                              data-testid={`dict-procedure-name-${idx}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#59554D] mb-1 block">
                              Key Parameters (optional)
                            </label>
                            <Input
                              value={proc.key_parameters || ""}
                              onChange={(e) =>
                                updateImportantProcedure(idx, "key_parameters", e.target.value)
                              }
                              placeholder="@DocketID int, @CustomerID int"
                              className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                              data-testid={`dict-procedure-params-${idx}`}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeImportantProcedure(idx)}
                          className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0] h-8 w-8 p-0 mt-5"
                          data-testid={`dict-procedure-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div>
                        <label className="text-xs text-[#59554D] mb-1 block">
                          What does this procedure do?
                        </label>
                        <Textarea
                          value={proc.description || ""}
                          onChange={(e) =>
                            updateImportantProcedure(idx, "description", e.target.value)
                          }
                          placeholder="Explain the function and what tables/logic it affects."
                          className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                          data-testid={`dict-procedure-description-${idx}`}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-[#59554D] mb-1 block">
                            When to use (optional)
                          </label>
                          <Textarea
                            value={proc.when_to_use || ""}
                            onChange={(e) =>
                              updateImportantProcedure(idx, "when_to_use", e.target.value)
                            }
                            placeholder="When would a support agent need this?"
                            className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                            data-testid={`dict-procedure-when-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#59554D] mb-1 block">
                            Notes / Gotchas (optional)
                          </label>
                          <Textarea
                            value={proc.notes || ""}
                            onChange={(e) =>
                              updateImportantProcedure(idx, "notes", e.target.value)
                            }
                            placeholder="Edge cases, common pitfalls, side effects..."
                            className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                            data-testid={`dict-procedure-notes-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Import Schema Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]">
          <DialogHeader>
            <DialogTitle>
              {importIsReimport ? "Re-import Database Schema" : "Import Database Schema"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#59554D] mb-2 block">
                {importIsReimport ? "Database" : "Select Database"}
              </label>
              {importIsReimport ? (
                <div
                  className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-sm font-mono"
                  data-testid="import-reimport-client"
                >
                  {importClientId}
                </div>
              ) : (
                <Select value={importClientId} onValueChange={setImportClientId}>
                  <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="import-database-select">
                    <SelectValue placeholder="Choose a database..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                    {availableForImport.map((config) => (
                      <SelectItem key={config.client_id} value={config.client_id}>
                        {config.client_id} - {config.database}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex items-start gap-3 bg-[#F4F2ED] rounded-lg p-3">
              <Switch
                checked={importIncludeProcedures}
                onCheckedChange={setImportIncludeProcedures}
                data-testid="import-include-procedures"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Include stored procedures</div>
                <div className="text-xs text-[#59554D] mt-1">
                  Imports the list of procedures and their parameters.
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-[#F4F2ED] rounded-lg p-3">
              <Switch
                checked={importIncludeDefinitions}
                onCheckedChange={setImportIncludeDefinitions}
                disabled={!importIncludeProcedures}
                data-testid="import-include-definitions"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Include stored procedure definitions</div>
                <div className="text-xs text-[#59554D] mt-1">
                  Required for AI procedure analysis. Adds time to the import.
                </div>
              </div>
            </div>
            
            {importIsReimport ? (
              <div className="bg-[#FFF8D6] border border-[#FFF8D6] rounded-lg p-3 text-sm text-[#59554D]">
                <CheckCircle className="w-4 h-4 inline mr-2 text-[#8A6D00]" />
                Re-import preserves admin annotations (modified flags, modification notes, AI summaries, affects-tables) and any manually-added procedures.
              </div>
            ) : (
              <div className="bg-[#F4F2ED] rounded-lg p-3 text-sm text-[#59554D]">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-[#6B5600]" />
                Schema import may take several minutes for large databases.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(false)}
              className="border-[#DAD5CA] text-[#59554D]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportSchema}
              disabled={!importClientId || importing}
              className="bg-[#FFD108] hover:bg-[#FFD108]"
              data-testid="import-confirm-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {importIsReimport ? "Re-importing..." : "Importing..."}
                </>
              ) : (
                <>
                  {importIsReimport ? (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  ) : (
                    <Database className="w-4 h-4 mr-2" />
                  )}
                  {importIsReimport ? "Re-import Schema" : "Import Schema"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Detail Dialog */}
      <Dialog open={tableDetailOpen} onOpenChange={setTableDetailOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TableIcon className="w-5 h-5 text-[#8A6D00]" />
              {selectedTable?.table?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTable && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-[#59554D]">
                <span>{selectedTable.table.columns?.length || 0} columns</span>
                <span>~{selectedTable.table.row_count_estimate?.toLocaleString() || 0} rows</span>
                <span>{selectedTable.relationships?.length || 0} relationships</span>
              </div>
              
              {/* Columns */}
              <div>
                <h4 className="font-medium mb-2">Columns</h4>
                <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#DAD5CA]">
                        <TableHead className="text-[#59554D]">Name</TableHead>
                        <TableHead className="text-[#59554D]">Type</TableHead>
                        <TableHead className="text-[#59554D]">Nullable</TableHead>
                        <TableHead className="text-[#59554D]">Key</TableHead>
                        <TableHead className="text-[#59554D]">References</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedTable.table.columns?.map((col) => (
                        <TableRow key={col.name} className="border-[#DAD5CA]">
                          <TableCell className="font-mono text-sm">{col.name}</TableCell>
                          <TableCell className="text-[#59554D] text-sm">
                            {col.data_type}
                            {col.max_length ? `(${col.max_length})` : ""}
                          </TableCell>
                          <TableCell>
                            {col.is_nullable ? (
                              <span className="text-[#59554D]">Yes</span>
                            ) : (
                              <span className="text-[#6B5600]">No</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {col.is_primary_key && (
                              <span className="text-[#FFD108] text-xs">PK</span>
                            )}
                            {col.is_foreign_key && (
                              <span className="text-[#8A6D00] text-xs">FK</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[#59554D] text-sm">
                            {col.references_table && (
                              <span>{col.references_table}.{col.references_column}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              {/* Relationships */}
              {selectedTable.relationships?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Relationships</h4>
                  <div className="space-y-2">
                    {selectedTable.relationships.map((rel, idx) => (
                      <div key={idx} className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{rel.from_table}</span>
                          <span className="text-[#8A857A]">→</span>
                          <span className="font-mono text-[#8A6D00]">{rel.to_table}</span>
                        </div>
                        <div className="text-xs text-[#8A857A] mt-1">
                          {rel.from_columns?.join(", ")} → {rel.to_columns?.join(", ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Query Dialog */}
      <Dialog open={addQueryDialogOpen} onOpenChange={setAddQueryDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Approved Query</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Query Name</label>
              <Input
                value={newQuery.name}
                onChange={(e) => setNewQuery({ ...newQuery, name: e.target.value })}
                placeholder="e.g., Get Docket Details"
                className="bg-[#FFFFFF] border-[#DAD5CA]"
              />
            </div>
            
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Description</label>
              <Input
                value={newQuery.description}
                onChange={(e) => setNewQuery({ ...newQuery, description: e.target.value })}
                placeholder="What does this query do?"
                className="bg-[#FFFFFF] border-[#DAD5CA]"
              />
            </div>
            
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">SQL Query</label>
              <Textarea
                value={newQuery.sql}
                onChange={(e) => setNewQuery({ ...newQuery, sql: e.target.value })}
                placeholder="SELECT TOP 100 * FROM webDocket WHERE DocketID = @DocketID"
                className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm min-h-[150px]"
              />
            </div>
            
            <div className="bg-[#F4F2ED] rounded-lg p-3 text-sm text-[#59554D]">
              <Code className="w-4 h-4 inline mr-2 text-[#8A6D00]" />
              Use @ParameterName for dynamic values (e.g., @DocketNumber, @CustomerID)
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddQueryDialogOpen(false)}
              className="border-[#DAD5CA] text-[#59554D]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddApprovedQuery}
              className="bg-[#0E6B3F] hover:bg-[#0E6B3F]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Procedure Detail Dialog */}
      <Dialog open={procDetailOpen} onOpenChange={setProcDetailOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-[#8A6D00]" />
              <span className="font-mono">{selectedProc?.name}</span>
              {selectedProc?.is_modified && (
                <Badge className="bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1] hover:bg-[#FFF7D1]">
                  <Flag className="w-3 h-3 mr-1" />
                  Modified
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedProc && (
            <div className="space-y-5">
              {/* Action bar */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => openProcEdit(selectedProc)}
                  variant="outline"
                  className="border-[#DAD5CA] text-[#1A1A1A] hover:bg-[#F4F2ED]"
                  data-testid="proc-detail-flag-btn"
                >
                  <Flag className="w-4 h-4 mr-2" />
                  {selectedProc.is_modified ? "Edit Modification" : "Flag as Modified"}
                </Button>
                <Button
                  onClick={() => openPullToDictionary(selectedProc)}
                  variant="outline"
                  className="border-[#FFF7D1] text-[#6B5600] hover:bg-[#FFF7D1] hover:text-[#6B5600]"
                  data-testid="proc-detail-pull-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Pull into Dictionary
                </Button>
                <Button
                  onClick={() => runProcedureAnalysis(selectedProc.name)}
                  disabled={analyzingProc === selectedProc.name}
                  className="bg-[#FFD108] hover:bg-[#FFD108]"
                  data-testid="proc-detail-analyze-btn"
                >
                  {analyzingProc === selectedProc.name ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Run AI Analysis
                    </>
                  )}
                </Button>
              </div>

              {/* Modification info */}
              {selectedProc.is_modified && (
                <div className="bg-[#FFF7D1] border border-[#FFF7D1] rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-[#6B5600] font-medium text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Flagged as Client-Modified
                  </div>
                  {selectedProc.modification_notes && (
                    <div>
                      <div className="text-xs text-[#59554D] mb-1">Notes:</div>
                      <p className="text-sm whitespace-pre-wrap">{selectedProc.modification_notes}</p>
                    </div>
                  )}
                  {selectedProc.affects_tables?.length > 0 && (
                    <div>
                      <div className="text-xs text-[#59554D] mb-1">Affects tables:</div>
                      <div className="flex flex-wrap gap-1">
                        {selectedProc.affects_tables.map((t) => (
                          <Badge key={t} className="bg-[#DAD5CA] text-[#1A1A1A] border-[#DAD5CA] hover:bg-[#DAD5CA] font-mono text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedProc.standard_version && (
                    <div className="text-xs text-[#59554D]">
                      Standard version reference: <span className="font-mono">{selectedProc.standard_version}</span>
                    </div>
                  )}
                </div>
              )}

              {/* AI Analysis (saved or just-run) */}
              {(procAnalysisResult || selectedProc.ai_summary) && (
                <div className="bg-[#FFF8D6] border border-[#FFF8D6] rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 text-[#8A6D00] font-medium text-sm">
                    <Sparkles className="w-4 h-4" />
                    AI Analysis
                    {selectedProc.ai_summary_date && !procAnalysisResult && (
                      <span className="text-xs text-[#59554D] font-normal">
                        ({formatDateTime(selectedProc.ai_summary_date)})
                      </span>
                    )}
                  </div>

                  {procAnalysisResult ? (
                    <>
                      {procAnalysisResult.summary && (
                        <p className="text-sm">{procAnalysisResult.summary}</p>
                      )}
                      {procAnalysisResult.purpose && (
                        <div>
                          <div className="text-xs text-[#59554D] mb-1">Purpose</div>
                          <p className="text-sm whitespace-pre-wrap">{procAnalysisResult.purpose}</p>
                        </div>
                      )}
                      {procAnalysisResult.tables_affected?.length > 0 && (
                        <div>
                          <div className="text-xs text-[#59554D] mb-1">Tables affected</div>
                          <div className="flex flex-wrap gap-1">
                            {procAnalysisResult.tables_affected.map((t) => (
                              <Badge key={t} className="bg-[#DAD5CA] text-[#1A1A1A] border-[#DAD5CA] hover:bg-[#DAD5CA] font-mono text-xs">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {procAnalysisResult.parameters_explained?.length > 0 && (
                        <div>
                          <div className="text-xs text-[#59554D] mb-1">Parameters</div>
                          <ul className="text-sm space-y-1">
                            {procAnalysisResult.parameters_explained.map((p, i) => (
                              <li key={i}>
                                <span className="font-mono text-[#8A6D00]">{p.name}</span>{" "}
                                <span className="text-[#59554D]">— {p.purpose}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {procAnalysisResult.potential_issues?.length > 0 && (
                        <div>
                          <div className="text-xs text-[#6B5600] mb-1">Potential issues</div>
                          <ul className="text-sm list-disc ml-5 space-y-1">
                            {procAnalysisResult.potential_issues.map((it, i) => (
                              <li key={i}>{it}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {procAnalysisResult.recommendations?.length > 0 && (
                        <div>
                          <div className="text-xs text-[#0E6B3F] mb-1">Recommendations</div>
                          <ul className="text-sm list-disc ml-5 space-y-1">
                            {procAnalysisResult.recommendations.map((it, i) => (
                              <li key={i}>{it}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {procAnalysisResult.error && (
                        <p className="text-sm text-[#C23A00]">{procAnalysisResult.error}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{selectedProc.ai_summary}</p>
                  )}
                </div>
              )}

              {/* Parameters */}
              {selectedProc.parameters?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Parameters</h4>
                  <div className="bg-[#FFFFFF] rounded-lg border border-[#DAD5CA] overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DAD5CA]">
                          <TableHead className="text-[#59554D]">Name</TableHead>
                          <TableHead className="text-[#59554D]">Type</TableHead>
                          <TableHead className="text-[#59554D]">Output</TableHead>
                          <TableHead className="text-[#59554D]">Default</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedProc.parameters.map((p, i) => (
                          <TableRow key={i} className="border-[#DAD5CA]">
                            <TableCell className="font-mono text-sm">@{p.name}</TableCell>
                            <TableCell className="text-[#59554D] text-sm">
                              {p.data_type}
                              {p.max_length ? `(${p.max_length})` : ""}
                            </TableCell>
                            <TableCell>
                              {p.is_output ? <span className="text-[#8A6D00] text-xs">OUT</span> : "-"}
                            </TableCell>
                            <TableCell className="text-[#59554D] text-sm">
                              {p.has_default ? (p.default_value ?? "(default)") : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Tables Referenced */}
              {selectedProc.tables_referenced?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Tables Referenced (from definition)</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedProc.tables_referenced.map((t) => (
                      <Badge key={t} className="bg-[#DAD5CA] text-[#1A1A1A] border-[#DAD5CA] hover:bg-[#DAD5CA] font-mono text-xs">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Definition */}
              {selectedProc.definition ? (
                <div>
                  <h4 className="font-medium mb-2">Definition</h4>
                  <pre className="text-xs bg-[#FFFFFF] border border-[#DAD5CA] p-3 rounded overflow-x-auto max-h-[300px] overflow-y-auto text-[#59554D]">
{selectedProc.definition}
                  </pre>
                </div>
              ) : (
                <div className="bg-[#F4F2ED] rounded-lg p-3 text-sm text-[#59554D]">
                  <AlertTriangle className="w-4 h-4 inline mr-2 text-[#6B5600]" />
                  Procedure definition not imported. Re-import the schema with "Include definitions" enabled to use AI analysis.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Procedure Edit (flag/modification) Dialog */}
      <Dialog open={procEditOpen} onOpenChange={setProcEditOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#6B5600]" />
              Flag Procedure as Modified
            </DialogTitle>
          </DialogHeader>

          {selectedProc && (
            <div className="space-y-4 py-2">
              <div className="font-mono text-sm text-[#59554D]">
                {selectedProc.schema_name}.{selectedProc.name}
              </div>

              <div className="flex items-center justify-between bg-[#F4F2ED] rounded-lg p-3">
                <div>
                  <div className="text-sm font-medium">Modified from standard</div>
                  <div className="text-xs text-[#59554D]">
                    Marks this procedure as deviating from the standard Allocate schema.
                  </div>
                </div>
                <Switch
                  checked={procEditForm.is_modified}
                  onCheckedChange={(v) => setProcEditForm({ ...procEditForm, is_modified: v })}
                  data-testid="proc-edit-is-modified"
                />
              </div>

              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Modification Notes
                </label>
                <Textarea
                  value={procEditForm.modification_notes}
                  onChange={(e) =>
                    setProcEditForm({ ...procEditForm, modification_notes: e.target.value })
                  }
                  placeholder="What was changed and why? What should support staff know?"
                  className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[120px]"
                  data-testid="proc-edit-notes"
                />
              </div>

              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Affects Tables (comma-separated)
                </label>
                <Input
                  value={procEditForm.affects_tables}
                  onChange={(e) =>
                    setProcEditForm({ ...procEditForm, affects_tables: e.target.value })
                  }
                  placeholder="webDocket, Booking, Customer"
                  className="bg-[#FFFFFF] border-[#DAD5CA]"
                  data-testid="proc-edit-affects-tables"
                />
                <p className="text-xs text-[#8A857A] mt-1">
                  Used by the AI to add this proc's context when those tables are queried.
                </p>
              </div>

              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Standard Version Reference (optional)
                </label>
                <Input
                  value={procEditForm.standard_version}
                  onChange={(e) =>
                    setProcEditForm({ ...procEditForm, standard_version: e.target.value })
                  }
                  placeholder="e.g., Allocate 2024.3"
                  className="bg-[#FFFFFF] border-[#DAD5CA]"
                  data-testid="proc-edit-standard-version"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProcEditOpen(false)}
              className="border-[#DAD5CA] text-[#59554D]"
              data-testid="proc-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveProcModification}
              disabled={saving}
              className="bg-[#0E6B3F] hover:bg-[#0E6B3F]"
              data-testid="proc-edit-save"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Pull procedure into Business Dictionary Dialog */}
      <Dialog open={pullDictOpen} onOpenChange={setPullDictOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#6B5600]" />
              Pull Procedure into Business Dictionary
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Business Area</label>
              <Select
                value={pullDictForm.area}
                onValueChange={(v) => setPullDictForm({ ...pullDictForm, area: v })}
              >
                <SelectTrigger
                  className="bg-[#FFFFFF] border-[#DAD5CA]"
                  data-testid="pull-dict-area-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                  {DICTIONARY_AREAS.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8A857A] mt-1">
                If a procedure with the same name already exists in this area, it will be replaced.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">Procedure Name</label>
                <Input
                  value={pullDictForm.name}
                  onChange={(e) =>
                    setPullDictForm({ ...pullDictForm, name: e.target.value })
                  }
                  className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                  data-testid="pull-dict-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Key Parameters
                </label>
                <Input
                  value={pullDictForm.key_parameters}
                  onChange={(e) =>
                    setPullDictForm({ ...pullDictForm, key_parameters: e.target.value })
                  }
                  className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                  data-testid="pull-dict-params-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#59554D] mb-1 block">
                What does this procedure do?
              </label>
              <Textarea
                value={pullDictForm.description}
                onChange={(e) =>
                  setPullDictForm({ ...pullDictForm, description: e.target.value })
                }
                placeholder="Pre-filled from AI summary if available. Edit as needed."
                className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[80px]"
                data-testid="pull-dict-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  When to use (optional)
                </label>
                <Textarea
                  value={pullDictForm.when_to_use}
                  onChange={(e) =>
                    setPullDictForm({ ...pullDictForm, when_to_use: e.target.value })
                  }
                  placeholder="When would a support agent need this?"
                  className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                  data-testid="pull-dict-when-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Notes / Gotchas (optional)
                </label>
                <Textarea
                  value={pullDictForm.notes}
                  onChange={(e) =>
                    setPullDictForm({ ...pullDictForm, notes: e.target.value })
                  }
                  placeholder="Edge cases, side effects..."
                  className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                  data-testid="pull-dict-notes-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPullDictOpen(false)}
              className="border-[#DAD5CA] text-[#59554D]"
              data-testid="pull-dict-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={savePullToDictionary}
              disabled={pullDictSaving}
              className="bg-[#FFF7D1] hover:bg-[#E68A00] text-black"
              data-testid="pull-dict-save"
            >
              {pullDictSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BookOpen className="w-4 h-4 mr-2" />
              )}
              Add to Dictionary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manual Add Stored Procedure Dialog */}
      <Dialog open={addProcOpen} onOpenChange={setAddProcOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#8A6D00]" />
              Add Stored Procedure Manually
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-[#F4F2ED] rounded-lg p-3 text-xs text-[#59554D]">
              <AlertTriangle className="w-4 h-4 inline mr-2 text-[#6B5600]" />
              Use this when the client database isn't reachable for automated import, or when you want to track a specific procedure. Auto-imported entries from a future schema re-import will not overwrite manual ones with the same name.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="text-sm text-[#59554D] mb-1 block">Procedure Name *</label>
                <Input
                  value={addProcForm.name}
                  onChange={(e) => setAddProcForm({ ...addProcForm, name: e.target.value })}
                  placeholder="e.g., sp_CreateDocket"
                  className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                  data-testid="add-proc-name"
                />
              </div>
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">Schema</label>
                <Input
                  value={addProcForm.schema_name}
                  onChange={(e) => setAddProcForm({ ...addProcForm, schema_name: e.target.value })}
                  placeholder="dbo"
                  className="bg-[#FFFFFF] border-[#DAD5CA] font-mono text-sm"
                  data-testid="add-proc-schema"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#59554D] mb-1 block">Description</label>
              <Textarea
                value={addProcForm.description}
                onChange={(e) => setAddProcForm({ ...addProcForm, description: e.target.value })}
                placeholder="What does this procedure do?"
                className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                data-testid="add-proc-description"
              />
            </div>

            <div>
              <label className="text-sm text-[#59554D] mb-1 block">
                Parameters (one per line)
              </label>
              <Textarea
                value={addProcForm.parameters_text}
                onChange={(e) => setAddProcForm({ ...addProcForm, parameters_text: e.target.value })}
                placeholder={"@DocketID int\n@CustomerID int\n@StartDate datetime"}
                className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[80px] font-mono text-sm"
                data-testid="add-proc-params"
              />
              <p className="text-xs text-[#8A857A] mt-1">
                Format: <span className="font-mono">@Name type</span> per line. Append <span className="font-mono">OUTPUT</span> for output params.
              </p>
            </div>

            <div>
              <label className="text-sm text-[#59554D] mb-1 block">
                CREATE PROCEDURE definition (optional)
              </label>
              <Textarea
                value={addProcForm.definition}
                onChange={(e) => setAddProcForm({ ...addProcForm, definition: e.target.value })}
                placeholder="CREATE PROCEDURE ... AS BEGIN ... END"
                className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[100px] font-mono text-xs"
                data-testid="add-proc-definition"
              />
              <p className="text-xs text-[#8A857A] mt-1">
                Required for AI analysis. Paste the full CREATE/ALTER statement if you have it.
              </p>
            </div>

            <div className="flex items-center justify-between bg-[#F4F2ED] rounded-lg p-3">
              <div>
                <div className="text-sm font-medium">Flag as modified from standard</div>
                <div className="text-xs text-[#59554D]">
                  Mark this procedure as client-specific / customised.
                </div>
              </div>
              <Switch
                checked={addProcForm.is_modified}
                onCheckedChange={(v) => setAddProcForm({ ...addProcForm, is_modified: v })}
                data-testid="add-proc-is-modified"
              />
            </div>

            {addProcForm.is_modified && (
              <div>
                <label className="text-sm text-[#59554D] mb-1 block">
                  Modification Notes
                </label>
                <Textarea
                  value={addProcForm.modification_notes}
                  onChange={(e) =>
                    setAddProcForm({ ...addProcForm, modification_notes: e.target.value })
                  }
                  placeholder="What was changed and why?"
                  className="bg-[#FFFFFF] border-[#DAD5CA] min-h-[60px]"
                  data-testid="add-proc-modification-notes"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddProcOpen(false)}
              className="border-[#DAD5CA] text-[#59554D]"
              data-testid="add-proc-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveManualProcedure}
              disabled={addProcSaving}
              className="bg-[#0E6B3F] hover:bg-[#0E6B3F]"
              data-testid="add-proc-save"
            >
              {addProcSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add Procedure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
